/**
 * Transport 抽象（§11.7）。
 *
 * 三种形态 —— 联机 WS、本地传手机、回放 —— 都实现这一个接口，产出**同构**的 `ServerMessage`。
 * 于是 UI 层（牌桌、行动条、动画）里不允许出现任何 `if (mode === 'local')` 的业务分支：
 * 模式差异被关在 Transport 里，上面只看得见一条消息流。
 *
 * ⚠️ 范围说明（与 §5.5 的偏差，见 deviations）：
 * §5.5 的完整 `ClientMessage` / `ServerMessage` 判别联合属于 `packages/shared/src/protocol/messages.ts`，
 * 那个文件在 M1 还不存在（shared 目前只有引擎）。为了让本地模式现在就能跑，这里先定义**同名同形状的 M1 子集**：
 * 判别符（`type` 的取值）与字段名都照抄 §5.5，将来 protocol 包落地时把本文件的类型换成 re-export 即可，
 * 调用方一行都不用改。子集里刻意**没有**任何联机专属的连接层字段（session / resume / heartbeat / 鉴权）。
 *
 * 另有两处刻意的取舍：
 *  1. 牌一律用引擎原生的 `CardId`（0..51 的整数），不做 §5.6 的 `CardId → Card` 字符串转换。
 *     本地模式两端在同一个进程里，转成 2 字符只是白白多一次编解码；
 *     `PublicHandState` 本身也是 CardId 的，保持一致能让 `buildTableVM` 只处理一种表示。
 *  2. 多了一条 `handSnapshot`。联机模式靠 `roomSnapshot` + 增量，本地引擎每一步都能直接给出
 *     **权威脱敏视图**，让 `tableStore.applyHandView` 有唯一真源；增量消息仍然照发，供动画层编排。
 */
import type {
  AppliedAction,
  CardId,
  Chips,
  HandId,
  HandRank,
  LegalActions,
  Millis,
  Position,
  Pot,
  PublicHandResult,
  PublicHandState,
  RoomConfig,
  SeatIndex,
  Street,
} from '@poker/shared'

export type TransportMode = 'online' | 'local' | 'replay'

/** 协议版本。本地模式不校验，但保留字段以免将来接 WS 时消息形状再变一次。 */
export const PROTOCOL_VERSION = 1

// ============================================================================
// 公共小形状
// ============================================================================

/**
 * 底牌的下发形状（§5.5）。**必须是判别联合**，不能写成 `cards: [Card,Card] | null` ——
 * 「有字段填 null」这种形式一旦某处忘了置空就是泄漏，而判别联合逼着接收方先分支。
 */
export type HoleCardsView =
  | { readonly visibility: 'hidden'; readonly count: 2 }
  | { readonly visibility: 'shown'; readonly cards: readonly [CardId, CardId] }

/** 座位的公开信息。本地模式的 `name` 来自 `LocalPlayer`，与账号体系无任何映射（§11.8）。 */
export interface SeatView {
  readonly seat: SeatIndex
  readonly name: string
  readonly stack: Chips
  readonly sittingOut: boolean
  readonly isBot: boolean
  readonly connected: boolean
}

/** 房间相位的 M1 子集。联机模式的完整 `RoomPhase` 还包含 paused / closed 等。 */
export type RoomPhaseLite = 'lobby' | 'running' | 'ended'

/** 玩家动作的线上表示。`to` 恒为「本轮累计投入总额」（raise-to 语义，红线 #18）。 */
export type ActionKind = 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'allIn'

// ============================================================================
// client → server
// ============================================================================

export type ClientMessage =
  /** 开始第一手（或从 ended 重开）。本地模式即「开始游戏」。 */
  | { readonly type: 'startGame' }
  /** 手牌结束后手动开下一手（`autoStart: false` 时用）。 */
  | { readonly type: 'nextHand' }
  /**
   * ★ 不带 `seat`：座位由 Transport 从「连接身份」解析（红线 #37）。
   * 本地模式的连接身份就是「当前持有行动权的座位」—— 拿着手机的那个人。
   * 客户端上报 seat 在本地模式下会直接变成「替别人下注」的通道。
   */
  | {
      readonly type: 'playerAction'
      readonly handId: HandId
      readonly action: ActionKind
      /** 仅 bet / raise 带；raise-to 总额 */
      readonly to?: Chips
      /** 幂等键：等于提交时的 `actionLog.length`。省略则不做陈旧校验。 */
      readonly actionNo?: number
      readonly clientActionId: string
    }
  | { readonly type: 'requestSnapshot'; readonly reason: 'gap' | 'desync' | 'manual' }
  /**
   * 切换观察视角。**只有 local / replay 形态接受**：联机模式的视角恒为自己的座位，
   * 由服务端裁剪，客户端说了不算。
   */
  | { readonly type: 'setPerspective'; readonly seat: SeatIndex | null }
  | { readonly type: 'leaveRoom' }

// ============================================================================
// server → client
// ============================================================================

export type ServerMessage =
  // —— 连接层 ——
  | {
      readonly type: 'welcome'
      readonly mode: TransportMode
      readonly protocolVersion: number
      readonly serverTime: Millis
    }
  | {
      readonly type: 'error'
      readonly code: string
      readonly message: string
      readonly retryable: boolean
      readonly refClientActionId?: string
    }

  // —— 房间 ——
  | {
      readonly type: 'roomSnapshot'
      readonly roomCode: string | null
      readonly config: RoomConfig
      readonly seats: readonly SeatView[]
      readonly phase: RoomPhaseLite
      readonly handNo: number
    }

  // —— 牌局：权威视图（本地模式独有，见文件头说明）——
  | {
      readonly type: 'handSnapshot'
      readonly handId: HandId
      /** 已按 `viewerSeat` 脱敏；这是客户端**唯一**拿得到牌面的通道 */
      readonly view: PublicHandState
      /** 仅当 `view.actionOn === view.viewerSeat` 时非 null */
      readonly legal: LegalActions | null
    }

  // —— 牌局增量（§5.6 映射表）——
  | {
      readonly type: 'handStarted'
      readonly handId: HandId
      readonly handNo: number
      readonly buttonSeat: SeatIndex
      readonly sbSeat: SeatIndex | null
      readonly bbSeat: SeatIndex
      readonly deadButton: boolean
      readonly seats: readonly {
        readonly seat: SeatIndex
        readonly stack: Chips
        readonly position: Position
      }[]
    }
  | {
      readonly type: 'blindsPosted'
      readonly handId: HandId
      readonly posts: readonly {
        readonly seat: SeatIndex
        readonly kind: 'sb' | 'bb' | 'ante' | 'dead'
        readonly amount: Chips
        readonly allIn: boolean
      }[]
    }
  | {
      readonly type: 'holeCardsDealt'
      readonly handId: HandId
      readonly seats: readonly { readonly seat: SeatIndex; readonly holeCards: HoleCardsView }[]
    }
  | {
      readonly type: 'turnChanged'
      readonly handId: HandId
      readonly actionNo: number
      readonly seat: SeatIndex
      /**
       * 本地传手机模式**恒为 null**（§11.8）：手机还在传递途中时间就走完了，
       * 引擎会按超时自动弃牌。倒计时环看到 null 就不渲染。
       */
      readonly displayDeadlineAt: Millis | null
      readonly hardDeadlineAt: Millis | null
    }
  /** 私有：只发给当前行动者。本地模式里「当前行动者」就是 perspectiveSeat。 */
  | {
      readonly type: 'actionPrompt'
      readonly handId: HandId
      readonly actionNo: number
      readonly legal: LegalActions
    }
  | {
      readonly type: 'playerActed'
      readonly handId: HandId
      readonly action: AppliedAction
      readonly stackAfter: Chips
    }
  | {
      readonly type: 'communityCards'
      readonly handId: HandId
      readonly street: Street
      readonly cards: readonly CardId[]
      readonly pacingHintMs: number
    }
  | {
      readonly type: 'uncalledBetReturned'
      readonly handId: HandId
      readonly seat: SeatIndex
      readonly amount: Chips
      readonly stackAfter: Chips
    }
  | {
      readonly type: 'potsUpdated'
      readonly handId: HandId
      readonly pots: readonly Pot[]
      readonly potTotal: Chips
    }
  | { readonly type: 'runItOut'; readonly handId: HandId; readonly remainingStreets: readonly Street[] }
  /** 他人底牌**唯一**的外泄闸门（红线 #1）：能走到这里说明引擎已判定该牌对全场公开。 */
  | {
      readonly type: 'cardsRevealed'
      readonly handId: HandId
      readonly seat: SeatIndex
      readonly cards: readonly [CardId, CardId]
      readonly rank: HandRank
      readonly voluntary: boolean
    }
  /** ★ 不带任何牌面字段。盖牌就是盖牌，连 category 都不给（红线 #3）。 */
  | { readonly type: 'cardsMucked'; readonly handId: HandId; readonly seat: SeatIndex }
  | { readonly type: 'handEnded'; readonly handId: HandId; readonly result: PublicHandResult }

  // —— 本地 / 回放专属 ——
  /**
   * 观察视角切换。这是本地传手机模式的关键信号：收到它就要
   * ① 立刻遮牌（不透明、零动画）、② 重建 equity worker、③ 所有牌强制回背面（§11.8 第 10 条）。
   * `seat === null` 表示旁观视角（摊牌后全桌一起看结果时用）。
   */
  | {
      readonly type: 'perspectiveChanged'
      readonly seat: SeatIndex | null
      readonly name: string | null
      readonly reason: 'turn' | 'handEnd' | 'manual'
    }

// ============================================================================
// Transport
// ============================================================================

export interface Transport {
  readonly mode: TransportMode
  connect(): Promise<void>
  send(msg: ClientMessage): void
  /** 返回取消订阅函数。多次订阅按注册顺序派发。 */
  onMessage(cb: (m: ServerMessage) => void): () => void
  close(): void
}

// ============================================================================
// 定时器抽象
// ============================================================================

export type TimerHandle = number

/**
 * 把 `setTimeout` 抽出来，纯粹是为了让「手牌流程驱动」可以被同步测试：
 * 测试注入 `createManualScheduler()` 就能一步步 pump，不需要假时钟，也不会有悬挂定时器。
 */
export interface Scheduler {
  schedule(fn: () => void, ms: number): TimerHandle
  cancel(handle: TimerHandle): void
}

export function createRealScheduler(): Scheduler {
  return {
    schedule: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as TimerHandle,
    cancel: (h) => {
      globalThis.clearTimeout(h as unknown as ReturnType<typeof setTimeout>)
    },
  }
}

export interface ManualScheduler extends Scheduler {
  /** 按注册顺序跑完所有待执行任务（忽略延迟）。返回实际执行的任务数。 */
  runAll(maxSteps?: number): number
  pending(): number
}

/** 测试用：任务不会自己跑，必须显式 `runAll()`，因此没有任何重入与时序不确定性。 */
export function createManualScheduler(): ManualScheduler {
  const tasks = new Map<TimerHandle, () => void>()
  let nextId: TimerHandle = 1
  return {
    schedule(fn) {
      const id = nextId++
      tasks.set(id, fn)
      return id
    },
    cancel(h) {
      tasks.delete(h)
    },
    pending: () => tasks.size,
    runAll(maxSteps = 1000) {
      let steps = 0
      while (tasks.size > 0 && steps < maxSteps) {
        const entry = tasks.entries().next()
        if (entry.done === true) break
        const [id, fn] = entry.value
        tasks.delete(id)
        steps++
        fn()
      }
      return steps
    },
  }
}
